// src/providers/SessionProvider.js
import React, { createContext, useContext, useEffect, useState } from 'react';
import { sessionService } from '../services/sessionService';

const SessionContext = createContext();

export const useSession = () => {
     const context = useContext(SessionContext);
     if (!context) {
          throw new Error('useSession must be used within a SessionProvider');
     }
     return context;
};

export const SessionProvider = ({ children }) => {
     const [sessionInfo, setSessionInfo] = useState({
          isActive: false,
          remainingMinutes: 0,
          isExpired: false
     });
     const [showWarning, setShowWarning] = useState(false);
     const [warningTime, setWarningTime] = useState(0);

     useEffect(() => {
          // Initialize session service when app starts
          const initSession = () => {
               const timeoutMinutes = parseInt(process.env.REACT_APP_SESSION_TIMEOUT_MINUTES) || 30;
               const warningMinutes = parseInt(process.env.REACT_APP_SESSION_WARNING_MINUTES) || 5;

               console.log('🔧 Environment variables:', {
                    REACT_APP_SESSION_TIMEOUT_MINUTES: process.env.REACT_APP_SESSION_TIMEOUT_MINUTES,
                    REACT_APP_SESSION_WARNING_MINUTES: process.env.REACT_APP_SESSION_WARNING_MINUTES,
                    timeoutMinutes,
                    warningMinutes
               });

               sessionService.init({
                    timeout: timeoutMinutes * 60 * 1000, // Use environment variable
                    warningTime: warningMinutes * 60 * 1000, // Use environment variable
                    onExpired: handleSessionExpired,
                    onWarning: handleSessionWarning
               });

               console.log(`Session timeout set to ${timeoutMinutes} minutes with ${warningMinutes} minute warning`);

               // Don't automatically request notification permission - only on user interaction
          };

          // Check if user is authenticated before starting session
          const token = localStorage.getItem('authToken');
          if (token) {
               initSession();
          }

          // Update session info periodically
          const updateInterval = setInterval(() => {
               if (sessionService.isActive) {
                    setSessionInfo(sessionService.getSessionInfo());
               }
          }, 1000);

          return () => {
               clearInterval(updateInterval);
               // Only stop tracking if component is truly unmounting, not just re-rendering
               sessionService.stopTracking();
          };
     }, []); // Empty dependency array to run only once

     const handleSessionExpired = () => {
          console.log('🔴 SessionProvider: Handling session expiration');
          setShowWarning(false);

          // Import and use the logout service directly
          import('../services/logoutService').then(({ logoutService }) => {
               console.log('🚪 Performing logout due to session expiration...');
               logoutService.performCompleteLogout({
                    redirectToLogout: false,
                    clearAllData: true
               }).then(() => {
                    console.log('✅ Session expiration logout completed');
                    window.location.replace('/?type=session_expired&reason=inactivity');
               }).catch((error) => {
                    console.error('❌ Session expiration logout failed:', error);
                    window.location.replace('/?type=emergency&reason=session_timeout');
               });
          });
     };

     const handleSessionWarning = (secondsLeft) => {
          setWarningTime(secondsLeft);
          setShowWarning(true);
     };

     const extendSession = () => {
          sessionService.extendSession();
          setShowWarning(false);
          setWarningTime(0);
     };

     const enableNotifications = async () => {
          try {
               const granted = await sessionService.requestNotificationPermission();
               if (granted) {
                    console.log('Notifications enabled successfully');
               }
          } catch (error) {
               console.log('Failed to enable notifications:', error);
          }
     };

     const dismissWarning = () => {
          setShowWarning(false);
          setWarningTime(0);
     };

     const value = {
          sessionInfo,
          showWarning,
          warningTime,
          extendSession,
          dismissWarning,
          enableNotifications,
          startSession: () => sessionService.startTracking(),
          stopSession: () => sessionService.stopTracking()
     };

     return (
          <SessionContext.Provider value={value}>
               {children}
               {showWarning && <SessionWarningModal />}
          </SessionContext.Provider>
     );
};

// Session warning modal component
const SessionWarningModal = () => {
     const { warningTime, extendSession, dismissWarning, enableNotifications } = useSession();
     const [countdown, setCountdown] = useState(warningTime);

     useEffect(() => {
          setCountdown(warningTime);
     }, [warningTime]);

     useEffect(() => {
          if (countdown <= 0) return;

          const timer = setTimeout(() => {
               setCountdown(countdown - 1);
          }, 1000);

          return () => clearTimeout(timer);
     }, [countdown]);

     const minutes = Math.floor(countdown / 60);
     const seconds = countdown % 60;

     return (
          <div className="session-warning-overlay">
               <div className="session-warning-modal">
                    <div className="session-warning-header">
                         <h3>⚠️ Session Expiring</h3>
                    </div>
                    <div className="session-warning-body">
                         <p>Your session will expire in:</p>
                         <div className="countdown-display">
                              {minutes > 0 && <span>{minutes}m </span>}
                              <span>{seconds}s</span>
                         </div>
                         <p>You will be automatically logged out due to inactivity.</p>
                    </div>
                    <div className="session-warning-actions">
                         <button
                              className="btn btn-primary"
                              onClick={extendSession}
                         >
                              Continue Session
                         </button>
                         <button
                              className="btn btn-secondary"
                              onClick={dismissWarning}
                         >
                              Dismiss
                         </button>
                         {Notification.permission === 'default' && (
                              <button
                                   className="btn btn-info"
                                   onClick={enableNotifications}
                                   style={{ marginTop: '8px', width: '100%' }}
                              >
                                   🔔 Enable Notifications
                              </button>
                         )}
                    </div>
               </div>

               <style>{`
        .session-warning-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
        }
        
        .session-warning-modal {
          background: white;
          border-radius: 12px;
          padding: 24px;
          max-width: 400px;
          width: 90%;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
          text-align: center;
        }
        
        .session-warning-header h3 {
          margin: 0 0 16px 0;
          color: #dc3545;
          font-size: 20px;
        }
        
        .session-warning-body {
          margin-bottom: 24px;
        }
        
        .countdown-display {
          font-size: 32px;
          font-weight: bold;
          color: #dc3545;
          margin: 16px 0;
          font-family: monospace;
        }
        
        .session-warning-actions {
          display: flex;
          gap: 12px;
          justify-content: center;
        }
        
        .btn {
          padding: 10px 20px;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
        }
        
        .btn-primary {
          background: #007bff;
          color: white;
        }
        
        .btn-primary:hover {
          background: #0056b3;
        }
        
        .btn-secondary {
          background: #6c757d;
          color: white;
        }
        
        .btn-secondary:hover {
          background: #545b62;
        }
        
        .btn-info {
          background: #17a2b8;
          color: white;
          font-size: 12px;
        }
        
        .btn-info:hover {
          background: #138496;
        }
      `}</style>
          </div>
     );
};

export default SessionProvider;
